// src/utils/driftDetector.js
// ============================================================
// DRIFT DETECTION
// Detects whether a tenant's payment behavior is improving,
// stable, or worsening over time. This is the early warning
// system that catches problems before they become delinquency.
// ============================================================

/**
 * Analyze payment drift for a tenant
 * @param {Array} paymentHistory - Array of { date, amount, daysLate }
 * @returns {Object} - { direction, velocity, description, alert }
 */
export function analyzeDrift(paymentHistory) {
  if (!paymentHistory || paymentHistory.length < 3) {
    return {
      direction: 'insufficient_data',
      velocity: 0,
      description: 'Not enough payment history to detect trends.',
      alert: false,
    };
  }

  // Use the last 6 payments for drift analysis
  const recent = paymentHistory.slice(-6);
  const daysLateValues = recent.map(p => p.daysLate || 0);

  // Calculate slope using simple linear regression
  const n = daysLateValues.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  daysLateValues.forEach((y, x) => {
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  });

  const denom = (n * sumX2) - (sumX * sumX);
  if (denom === 0) {
    return { direction: 'stable', velocity: 0, description: 'Payment timing is consistent.', alert: false };
  }

  const slope = ((n * sumXY) - (sumX * sumY)) / denom;
  const velocity = Math.round(slope * 10) / 10; // days-late change per month

  // Classify the drift direction
  if (velocity > 1.5) {
    return {
      direction: 'worsening',
      velocity,
      description: `Payment timing is getting worse — averaging ${velocity} more days late each month.`,
      alert: true,
    };
  } else if (velocity > 0.5) {
    return {
      direction: 'slightly_worsening',
      velocity,
      description: `Payment timing is drifting later — trending ${velocity} days per month.`,
      alert: true,
    };
  } else if (velocity < -1.5) {
    return {
      direction: 'improving',
      velocity,
      description: `Payment timing is improving — averaging ${Math.abs(velocity)} fewer days late each month.`,
      alert: false,
    };
  } else if (velocity < -0.5) {
    return {
      direction: 'slightly_improving',
      velocity,
      description: `Payment timing is getting slightly better over time.`,
      alert: false,
    };
  } else {
    return {
      direction: 'stable',
      velocity,
      description: 'Payment timing is consistent month over month.',
      alert: false,
    };
  }
}


/**
 * Generate a sparkline-friendly array of days-late values
 * for visual display in the Action Feed cards
 * @param {Array} paymentHistory - Last 6-12 payments
 * @returns {Array} - Array of { month, daysLate } for charting
 */
export function getDriftSparklineData(paymentHistory) {
  if (!paymentHistory) return [];

  return paymentHistory.slice(-6).map((p, i) => ({
    month: i + 1,
    daysLate: p.daysLate || 0,
  }));
}
